import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Button,
  Box,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
} from '@mui/material';
import {
  Close as CloseIcon,
  Description as DescriptionIcon,
  Send as SendIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import EmailTemplateManager from './EmailTemplateManager';

// 将模板数据定义为常量
const DEFAULT_EMAIL_TEMPLATES = [
  {
    id: 1,
    name: 'Application Review Request',
    subject: 'Insurance Application Review Required - [Client Name]',
    content: `Dear [Underwriter Name],

I hope this email finds you well. I am submitting an insurance application for your review.

Client Details:
- Name: [Client Name]
- Policy Type: [Policy Type]
- Application ID: [Application ID]

The signed application documents are attached for your review. Please assess the application based on our standard underwriting criteria and upload the reviewed documents to our system.

Key points for review:
- Coverage requirements
- Risk assessment
- Special conditions (if any)

Please complete your review at your earliest convenience. If you need any additional information or clarification, please don't hesitate to contact me.

Best regards,
[Your Name]
Insurance Broker
[Your Phone]
[Your Email]`
  },
  {
    id: 2,
    name: 'Insurance Confirmation',
    subject: 'Your Insurance Application Confirmation - [Policy Type]',
    content: `Dear [Client Name],

Thank you for choosing us for your insurance needs. This email confirms that we have received and processed your insurance application.

Policy Details:
- Policy Type: [Policy Type]
- Policy Number: [Policy Number]
- Effective Date: [Effective Date]
- Coverage Period: [Coverage Period]

Attached Documents:
- Insurance Policy Certificate
- Coverage Summary
- Payment Schedule

Please review all documents carefully and keep them for your records. Your policy will be effective from the date mentioned above, subject to premium payment.

Important Notes:
- Please ensure all information is accurate
- Keep these documents in a safe place
- Contact us immediately if you notice any discrepancies

If you have any questions about your coverage or need to make changes to your policy, please don't hesitate to contact me.

Thank you for your business. We look forward to serving your insurance needs.

Best regards,
[Your Name]
Insurance Broker
[Your Phone]
[Your Email]

This email and any files transmitted with it are confidential and intended solely for the use of the individual or entity to whom they are addressed.`
  }
];

const EmailDialog = ({ open, onClose, transaction }) => {
  const [emailTemplates, setEmailTemplates] = useState(DEFAULT_EMAIL_TEMPLATES);
  // 确保始终有一个有效的选中模板
  const [selectedTemplate, setSelectedTemplate] = useState(emailTemplates[0] || {
    id: 0,
    name: '',
    subject: '',
    content: ''
  });
  
  // 使用选中模板初始化邮件数据，如果没有模板则使用空值
  const [emailData, setEmailData] = useState({
    to: transaction?.recipients || '',
    cc: '',
    subject: selectedTemplate?.subject || '',
    message: selectedTemplate?.content || ''
  });

  const [openTemplateManager, setOpenTemplateManager] = useState(false);

  const attachedDocuments = [
    {
      name: 'Signed_Insurance_Policy.pdf',
      size: '2.4 MB'
    },
    {
      name: 'Coverage_Summary.pdf',
      size: '1.1 MB'
    }
  ];

  const handleTemplateChange = (event) => {
    const template = emailTemplates.find(t => t.id === event.target.value) || {
      id: 0,
      name: '',
      subject: '',
      content: ''
    };
    setSelectedTemplate(template);
    setEmailData(prev => ({
      ...prev,
      subject: template.subject || '',
      message: template.content || ''
    }));
  };

  const handleInputChange = (field) => (event) => {
    setEmailData({
      ...emailData,
      [field]: event.target.value
    });
  };

  const handleSend = () => {
    // 处理发送邮件的逻辑
    console.log('Sending email:', emailData);
    console.log('Attached documents:', attachedDocuments);
    onClose();
  };

  const handleSaveTemplates = (newTemplates) => {
    setEmailTemplates(newTemplates);
    // 如果没有模板了，使用空模板
    if (newTemplates.length === 0) {
      setSelectedTemplate({
        id: 0,
        name: '',
        subject: '',
        content: ''
      });
      setEmailData(prev => ({
        ...prev,
        subject: '',
        message: ''
      }));
    } 
    // 如果当前选中的模板被删除，选择第一个可用的模板
    else if (!newTemplates.find(t => t.id === selectedTemplate.id)) {
      setSelectedTemplate(newTemplates[0]);
      setEmailData(prev => ({
        ...prev,
        subject: newTemplates[0].subject,
        message: newTemplates[0].content
      }));
    }
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle 
          sx={{ 
            borderBottom: '1px solid #e0e0e0',
            mb: 0,
            pb: 2,
            pt: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography variant="h6">Send Documents via Email</Typography>
          <IconButton
            edge="end"
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ height: 20 }} />
          
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <FormControl fullWidth>
                  <InputLabel>Email Template</InputLabel>
                  <Select
                    value={selectedTemplate?.id || ''}
                    onChange={handleTemplateChange}
                    label="Email Template"
                    disabled={emailTemplates.length === 0}
                  >
                    {emailTemplates.map((template) => (
                      <MenuItem key={template.id} value={template.id}>
                        {template.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <IconButton
                  onClick={() => setOpenTemplateManager(true)}
                  sx={{ 
                    alignSelf: 'center',
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    },
                  }}
                >
                  <SettingsIcon />
                </IconButton>
              </Box>

              <TextField
                required
                fullWidth
                label="To"
                value={emailData.to}
                onChange={handleInputChange('to')}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Cc"
                value={emailData.cc}
                onChange={handleInputChange('cc')}
                sx={{ mb: 2 }}
              />

              <TextField
                required
                fullWidth
                label="Subject"
                value={emailData.subject}
                onChange={handleInputChange('subject')}
                sx={{ mb: 2 }}
              />

              <TextField
                required
                fullWidth
                label="Message"
                multiline
                rows={8}
                value={emailData.message}
                onChange={handleInputChange('message')}
              />
            </FormControl>

            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2,
                backgroundColor: '#f8f9fa'
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Attached Documents
              </Typography>
              {attachedDocuments.map((doc, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1
                  }}
                >
                  <DescriptionIcon color="primary" sx={{ fontSize: 20 }} />
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {doc.name}
                  </Typography>
                  <Chip 
                    label={doc.size} 
                    size="small"
                    sx={{ 
                      backgroundColor: '#e3f2fd',
                      color: '#1976d2'
                    }}
                  />
                </Box>
              ))}
            </Paper>
          </Box>
        </DialogContent>

        <DialogActions sx={{ 
          p: 3,
          borderTop: '1px solid #e0e0e0',
          gap: 1
        }}>
          <Button 
            onClick={onClose}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={handleSend}
            sx={{
              backgroundColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#1565c0',
              },
            }}
          >
            Send Email
          </Button>
        </DialogActions>
      </Dialog>

      <EmailTemplateManager
        open={openTemplateManager}
        onClose={() => setOpenTemplateManager(false)}
        templates={emailTemplates}
        onSaveTemplates={handleSaveTemplates}
      />
    </>
  );
};

export default EmailDialog; 